import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import * as Sentry from '@sentry/react';
import amplitude from 'amplitude-js';
import { toggleModalWithTimer } from 'apps/auth/actions/appAction';
import { toggleChat } from 'apps/auth/actions/chatAction';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import { AF_RECIPIENT_ONFIDO_IN_REVIEW_PROMO_DONE_CLICKED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { SCENARIO_ID } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { KYC_FAILED_SCREEN, KYC_VERIFYING_SCREEN, ONFIDO_SDK } from 'apps/auth/constants/ComponentNames';
import { LANDING_LINK } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { addOnfido } from 'apps/auth/helpers/DOM';
import { useExtra, usePageMessages } from 'apps/auth/hooks/selectors';
import { useThemeDetector } from 'apps/auth/hooks/theme';
import { OnfidoContainer } from 'apps/auth/pages/kyc/OnfidoFramePage';
import { OnfidoService } from 'apps/auth/services/OnfidoService';

const AntiFraudRecipientOnlyMotion = (props) => {
    const ANTIFRAUD_SCENARIO_ID = 500;

    const extra = useExtra();
    const [screen, setScreen] = useState(ONFIDO_SDK);

    const onfidoOutRef = useRef(null);
    const messages = usePageMessages();
    const paymentInfo = extra.paymentInfo;
    const { theme } = useThemeDetector();

    useEffect(() => {
        if (typeof window !== 'undefined') {
            if (typeof window.Onfido === 'undefined') {
                addOnfido(() => {
                    initOnfido();
                });
            } else {
                initOnfido();
            }
        }
        return () => {
            onfidoOutRef.current?.tearDown();
        };
    }, []);

    const initOnfido = () => {
        let options = OnfidoService.getMotionFraudOption();

        onfidoOutRef.current = OnfidoService.init(window.Onfido, extra.onfidoAccessToken, options, onComplete, onError, theme);
    };

    const onComplete = () => {
        apiFetchData(`/antifraud/onfido/recipient/uploaded/document`, getRecipientFieldsForOnfido());

        props.toggleModalWithTimer(true, messages.submittedSpinner, () => {
            setScreen(KYC_VERIFYING_SCREEN);
        });
    };

    const onError = (data) => {
        Sentry.captureMessage('Recipient Onfido Frame error', {
            extra: {
                countryId: extra.paymentInfo?.recipientCountry,
                data: data,
            },
            level: 'error',
        });

        setScreen(KYC_FAILED_SCREEN);
    };

    const onClickBackIcon = () => {
        window.location.href = LANDING_LINK;
    };

    const getRecipientFieldsForOnfido = () => {
        return [
            {
                name: 'uuid',
                value: paymentInfo.uuid,
            },
            {
                name: 'currentScenarioId',
                value: paymentInfo.currentScenarioId,
            },
        ];
    };

    const openChat = (e) => {
        e.preventDefault();
        props.toggleChat(true);
    };

    const componentMap = {
        [ONFIDO_SDK]: (
            <CommonPage backUrl={LANDING_LINK} onClickIcon={onClickBackIcon}>
                {' '}
                <OnfidoContainer id="onfido-mount" />{' '}
            </CommonPage>
        ),
        [KYC_VERIFYING_SCREEN]: (
            <InfoPage
                iconName="clock-80"
                iconColor="primary"
                headerText={messages.verifyScreenTitle}
                secondaryText={<DangerouslyHtml dangerouslyHtml={messages.verifyScreenText} />}
                primaryButtonCaption={messages.gotItButton}
                primaryButtonType="link"
                primaryButtonLink={LANDING_LINK}
                primaryButtonOnClick={() => {
                    amplitude.getInstance().logEvent(AF_RECIPIENT_ONFIDO_IN_REVIEW_PROMO_DONE_CLICKED, { [SCENARIO_ID]: ANTIFRAUD_SCENARIO_ID });
                }}
            />
        ),
        [KYC_FAILED_SCREEN]: (
            <CommonPage>
                <InfoPage
                    iconName={'id-card-cross-80'}
                    iconColor="primary"
                    headerText={<DangerouslyHtml dangerouslyHtml={messages.onfidoErrorTitle} />}
                    buttonRowCaption={messages.chatWithSupportText}
                    buttonRowOnClick={openChat}
                    buttonRowType="link"
                    primaryButtonCaption={messages.gotItButton}
                    primaryButtonType="link"
                    primaryButtonLink={LANDING_LINK}
                />
            </CommonPage>
        ),
    };

    return <>{screen ? componentMap[screen] : null}</>;
};
export default connect(null, {
    toggleChat,
    toggleModalWithTimer,
})(AntiFraudRecipientOnlyMotion);
